import React from 'react';
import { motion } from 'framer-motion';
import { Code,Code2, Database, Cloud, Terminal } from 'lucide-react';

const SkillCategory = ({ title, skills, icon: Icon }) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const skillVariants = {
    hidden: { 
      opacity: 0,
      y: 20,
      scale: 0.95
    },
    show: { 
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  return (
    <motion.div
      initial="hidden"
      whileInView="show"
      viewport={{ once: true }}
      variants={containerVariants}
      className="bg-gradient-to-br from-[#101C10]/40 to-[#112811]/40 backdrop-blur-sm 
                 rounded-2xl p-6 border border-[#2fe22f]/10 hover:border-[#2fe22f]/30 
                 transition-all duration-500"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-[#2fe22f]/10 rounded-lg">
          <Icon className="w-6 h-6 text-[#2fe22f]" />
        </div>
        <h3 className="text-xl font-bold text-[#2fe22f]">{title}</h3>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        {skills.map((skill, index) => (
          <motion.div
            key={skill}
            variants={skillVariants}
            whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
            className="relative group"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-[#2fe22f]/0 to-[#2fe22f]/0 
                           group-hover:from-[#2fe22f]/5 group-hover:to-[#2fe22f]/10 
                           rounded-lg transition-all duration-500" />
            <div className="relative p-3 rounded-lg">
              <p className="text-gray-300 font-medium">{skill}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

const Skills = () => {
  const skillCategories = [
    {
      title: "Languages",
      icon: Code2,
      skills: ["C++", "JavaScript", "Java", "Python"]
    },
    {
      title: "Web Technologies",
      icon: Terminal,
      skills: ["React","Tailwind","Langchain","NextJS","Express","NodeJS"]
    },
    {
      title: "Database & Cloud",
      icon: Database,
      skills: ["MongoDB", "Postgres", "MySQL", "Firebase"]
    },
    {
      title: "Tools",
      icon: Cloud,
      skills: ["Linux", "Git", "VS Code", "GitHub"]
    }
  ];

  return (
    <section className="w-full font-lato min-h-screen bg-gradient-to-br from-[#101C10] to-[#112811] py-20" id='skills'>
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="container mx-auto px-6 max-w-6xl"
      >
        <div className="flex items-center gap-4 mb-12">
          <motion.div 
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
            className="w-12 h-1 bg-[#2fe22f]" 
          />
          <h2 className="text-4xl font-bold text-[#2fe22f]">Skills</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {skillCategories.map((category) => (
            <SkillCategory
              key={category.title}
              title={category.title}
              skills={category.skills}
              icon={category.icon}
            />
          ))}
        </div>
      </motion.div>
    </section>
  );
};

export default Skills;